/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * Invite Code Detail Modal - Neo-Cyberpunk Code Display
 * ============================================================================
 *
 * A futuristic, high-tech invite code display with holographic effects,
 * animated scan lines, and print-optimized layout.
 */

import React, { useRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Printer, CheckCircle, XCircle, Clock, AlertTriangle, Zap, Key, Users, Percent, Calendar, Copy } from 'lucide-react';
import { InviteCode, InviteCodeStatus } from '@/types/superadmin.types';

interface InviteCodeDetailModalProps {
  inviteCode: InviteCode | null;
  isOpen: boolean;
  onClose: () => void;
}

// Futuristic timestamp formatter
const formatFuturisticDate = (dateStr: string | undefined) => {
  if (!dateStr) return '----.--.--.--:--:--';
  const date = new Date(dateStr);
  return date.toISOString().replace('T', ' // ').replace('Z', ' UTC');
};

// Generate a fake barcode pattern for visual effect
const generateBarcode = (text: string): string[] => {
  const pattern: string[] = [];
  for (let i = 0; i < 40; i++) {
    pattern.push(Math.random() > 0.5 ? 'thin' : 'thick');
  }
  return pattern;
};

// Status configuration with cyberpunk colors
const getStatusConfig = (t: (key: string) => string): Record<InviteCodeStatus, {
  label: string;
  color: string;
  glow: string;
  icon: typeof CheckCircle;
  bgGradient: string;
}> => ({
  ACTIVE: {
    label: t('inviteCodeModal.status.active'),
    color: '#00ff88',
    glow: '0 0 20px rgba(0,255,136,0.5)',
    icon: CheckCircle,
    bgGradient: 'linear-gradient(135deg, rgba(0,255,136,0.1) 0%, transparent 50%)'
  },
  EXPIRED: {
    label: t('inviteCodeModal.status.expired'),
    color: '#666688',
    glow: '0 0 20px rgba(102,102,136,0.5)',
    icon: Clock,
    bgGradient: 'linear-gradient(135deg, rgba(102,102,136,0.1) 0%, transparent 50%)'
  },
  EXHAUSTED: {
    label: t('inviteCodeModal.status.exhausted'),
    color: '#ffaa00',
    glow: '0 0 20px rgba(255,170,0,0.5)',
    icon: AlertTriangle,
    bgGradient: 'linear-gradient(135deg, rgba(255,170,0,0.1) 0%, transparent 50%)'
  },
  DISABLED: {
    label: t('inviteCodeModal.status.disabled'),
    color: '#ff4466',
    glow: '0 0 20px rgba(255,68,102,0.5)',
    icon: XCircle,
    bgGradient: 'linear-gradient(135deg, rgba(255,68,102,0.1) 0%, transparent 50%)'
  },
});

export const InviteCodeDetailModal: React.FC<InviteCodeDetailModalProps> = ({
  inviteCode,
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation('platform');
  const printRef = useRef<HTMLDivElement>(null);
  const [scanLinePosition, setScanLinePosition] = useState(0);
  const [isAnimating, setIsAnimating] = useState(true);
  const [copied, setCopied] = useState(false);

  // Animated scan line effect
  useEffect(() => {
    if (!isOpen || !isAnimating) return;
    const interval = setInterval(() => {
      setScanLinePosition(prev => (prev + 1) % 100);
    }, 30);
    return () => clearInterval(interval);
  }, [isOpen, isAnimating]);

  // Stop animation after initial reveal
  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      setCopied(false);
      const timeout = setTimeout(() => setIsAnimating(false), 3000);
      return () => clearTimeout(timeout);
    }
  }, [isOpen]);

  const handleCopyCode = () => {
    if (inviteCode?.code) {
      navigator.clipboard.writeText(inviteCode.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handlePrint = () => {
    const printContent = printRef.current;
    if (!printContent) return;

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Invite Code - ${inviteCode?.code}</title>
          <link href="https://fonts.googleapis.com/css2?family=Orbitron:wght@400;500;600;700;800;900&family=JetBrains+Mono:wght@300;400;500;600&family=Inter:wght@300;400;500;600&display=swap" rel="stylesheet">
          <style>
            ${printStyles}
          </style>
        </head>
        <body>
          ${printContent.innerHTML}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  if (!isOpen || !inviteCode) return null;

  const statusConfig = getStatusConfig(t);
  const status = statusConfig[inviteCode.status] || statusConfig.ACTIVE;
  const StatusIcon = status.icon;
  const barcode = generateBarcode(inviteCode.code);
  const usagePercent = inviteCode.maxUses
    ? Math.round((inviteCode.currentUses / inviteCode.maxUses) * 100)
    : null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <div
          className="pointer-events-auto w-full max-w-2xl max-h-[90vh] overflow-auto"
          style={{
            animation: 'modalSlideIn 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
          }}
        >
          {/* Action buttons - outside printable area */}
          <div className="flex justify-end gap-2 mb-3">
            <button
              onClick={handlePrint}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-all duration-300"
              style={{
                background: 'linear-gradient(135deg, #1a1a2e 0%, #0f0f1a 100%)',
                border: '1px solid rgba(255,170,0,0.3)',
                color: '#ffaa00',
                boxShadow: '0 0 15px rgba(255,170,0,0.2)',
              }}
            >
              <Printer className="w-4 h-4" />
              {t('inviteCodeModal.actions.print')}
            </button>
            <button
              onClick={onClose}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-all duration-300"
              style={{
                background: 'linear-gradient(135deg, #2a1a1a 0%, #1a0f0f 100%)',
                border: '1px solid rgba(255,68,102,0.3)',
                color: '#ff4466',
              }}
            >
              <X className="w-4 h-4" />
              {t('inviteCodeModal.actions.close')}
            </button>
          </div>

          {/* Printable Content */}
          <div ref={printRef} className="receipt-container">
            <style>{modalStyles}</style>

            <div className="cyber-receipt">
              {/* Scan line effect */}
              {isAnimating && (
                <div
                  className="scan-line"
                  style={{ top: `${scanLinePosition}%` }}
                />
              )}

              {/* Circuit pattern overlay */}
              <div className="circuit-pattern" />

              {/* Header */}
              <div className="receipt-header">
                <div className="header-left">
                  <div className="logo-container">
                    <div className="logo-hex">
                      <Zap className="logo-icon" />
                    </div>
                    <div className="logo-text">
                      <span className="brand-name">{t('inviteCodeModal.brand.name')}</span>
                      <span className="brand-sub">{t('inviteCodeModal.brand.subtitle')}</span>
                    </div>
                  </div>
                </div>
                <div className="header-right">
                  <div className="doc-type">{t('inviteCodeModal.docType')}</div>
                  <div className="doc-id">REF-{inviteCode.id?.slice(0, 6).toUpperCase()}</div>
                </div>
              </div>

              {/* Status Banner */}
              <div
                className="status-banner"
                style={{
                  borderColor: status.color,
                  boxShadow: status.glow,
                  background: status.bgGradient,
                }}
              >
                <div className="status-icon-container" style={{ color: status.color }}>
                  <StatusIcon className="status-icon" />
                  <div className="status-ring" style={{ borderColor: status.color }} />
                </div>
                <div className="status-text">
                  <span className="status-label" style={{ color: status.color }}>
                    {status.label}
                  </span>
                  <span className="status-time">
                    {t('inviteCodeModal.fields.created')}: {formatFuturisticDate(inviteCode.createdAt)}
                  </span>
                </div>
              </div>

              {/* Code Display Section */}
              <div className="code-display-section">
                <div className="code-label">{t('inviteCodeModal.codeLabel')}</div>
                <div className="code-value-container">
                  <div className="code-value">{inviteCode.code}</div>
                  <button className="copy-button" onClick={handleCopyCode}>
                    {copied ? (
                      <CheckCircle className="w-5 h-5" />
                    ) : (
                      <Copy className="w-5 h-5" />
                    )}
                    <span>{copied ? t('inviteCodeModal.actions.copied') : t('inviteCodeModal.actions.copy')}</span>
                  </button>
                </div>
                {inviteCode.description && (
                  <div className="code-description">{inviteCode.description}</div>
                )}
              </div>

              {/* Main Content Grid */}
              <div className="content-grid">
                {/* Left Column - Code Details */}
                <div className="detail-section">
                  <div className="section-header">
                    <Key className="section-icon" />
                    <span>{t('inviteCodeModal.sections.codeDetails')}</span>
                  </div>
                  <div className="detail-rows">
                    <div className="detail-row">
                      <span className="detail-label">{t('inviteCodeModal.fields.codeId')}</span>
                      <span className="detail-value mono">{inviteCode.id?.slice(0, 12)}...</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">{t('inviteCodeModal.fields.partner')}</span>
                      <span className="detail-value highlight">{inviteCode.partnerName || t('inviteCodeModal.noPartner')}</span>
                    </div>
                    {inviteCode.expiresAt && (
                      <div className="detail-row">
                        <span className="detail-label">{t('inviteCodeModal.fields.expires')}</span>
                        <span className="detail-value">{new Date(inviteCode.expiresAt).toLocaleDateString()}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Right Column - Usage Stats */}
                <div className="detail-section">
                  <div className="section-header">
                    <Users className="section-icon" />
                    <span>{t('inviteCodeModal.sections.usageStatistics')}</span>
                  </div>
                  <div className="detail-rows">
                    <div className="detail-row">
                      <span className="detail-label">{t('inviteCodeModal.fields.currentUses')}</span>
                      <span className="detail-value highlight">{inviteCode.currentUses}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">{t('inviteCodeModal.fields.maxUses')}</span>
                      <span className="detail-value">{inviteCode.maxUses || t('inviteCodeModal.unlimited')}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">{t('inviteCodeModal.fields.commissionRate')}</span>
                      <span className="detail-value">{inviteCode.commissionRate > 0 ? `${inviteCode.commissionRate}%` : t('inviteCodeModal.none')}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Usage Progress Bar (if max uses defined) */}
              {inviteCode.maxUses && (
                <div className="progress-section">
                  <div className="progress-header">
                    <span className="progress-label">{t('inviteCodeModal.usageProgress')}</span>
                    <span className="progress-value">{usagePercent}%</span>
                  </div>
                  <div className="progress-bar-container">
                    <div
                      className="progress-bar-fill"
                      style={{ width: `${usagePercent}%` }}
                    />
                  </div>
                  <div className="progress-text">
                    {t('inviteCodeModal.usesConsumed', { current: inviteCode.currentUses, max: inviteCode.maxUses })}
                  </div>
                </div>
              )}

              {/* Stats Row */}
              <div className="stats-row">
                <div className="stat-item">
                  <div className="stat-icon">
                    <Users className="w-5 h-5" />
                  </div>
                  <div className="stat-content">
                    <div className="stat-value">{inviteCode.currentUses}</div>
                    <div className="stat-label">{t('inviteCodeModal.statsRow.redemptions')}</div>
                  </div>
                </div>
                <div className="stat-item">
                  <div className="stat-icon">
                    <Percent className="w-5 h-5" />
                  </div>
                  <div className="stat-content">
                    <div className="stat-value">{inviteCode.commissionRate}%</div>
                    <div className="stat-label">{t('inviteCodeModal.statsRow.commission')}</div>
                  </div>
                </div>
                <div className="stat-item">
                  <div className="stat-icon">
                    <Calendar className="w-5 h-5" />
                  </div>
                  <div className="stat-content">
                    <div className="stat-value">{inviteCode.status}</div>
                    <div className="stat-label">{t('inviteCodeModal.statsRow.status')}</div>
                  </div>
                </div>
              </div>

              {/* Barcode Section */}
              <div className="barcode-section">
                <div className="barcode-container">
                  {barcode.map((bar, i) => (
                    <div
                      key={i}
                      className={`barcode-bar ${bar}`}
                      style={{ animationDelay: `${i * 20}ms` }}
                    />
                  ))}
                </div>
                <div className="barcode-text">{inviteCode.code}</div>
              </div>

              {/* Footer */}
              <div className="receipt-footer">
                <div className="footer-grid">
                  <div className="footer-item">
                    <span className="footer-label">{t('inviteCodeModal.footer.created')}</span>
                    <span className="footer-value">{formatFuturisticDate(inviteCode.createdAt)}</span>
                  </div>
                  <div className="footer-item">
                    <span className="footer-label">{t('inviteCodeModal.footer.expires')}</span>
                    <span className="footer-value">{inviteCode.expiresAt ? formatFuturisticDate(inviteCode.expiresAt) : t('inviteCodeModal.never')}</span>
                  </div>
                  <div className="footer-item">
                    <span className="footer-label">{t('inviteCodeModal.footer.verification')}</span>
                    <span className="footer-value verification">
                      {inviteCode.id?.slice(0, 12).toUpperCase()}
                    </span>
                  </div>
                </div>
                <div className="footer-line" />
                <div className="footer-disclaimer">
                  {t('inviteCodeModal.footer.disclaimer')}
                </div>
                <div className="footer-brand">
                  <span className="powered-by">{t('inviteCodeModal.footer.poweredBy')}</span>
                  <span className="brand">COREX-SUITE</span>
                  <span className="year">&copy; {new Date().getFullYear()}</span>
                </div>
              </div>

              {/* Corner decorations */}
              <div className="corner corner-tl" />
              <div className="corner corner-tr" />
              <div className="corner corner-bl" />
              <div className="corner corner-br" />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

// Modal styles (displayed in browser) - Orange/Amber theme
const modalStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Orbitron:wght@400;500;600;700;800;900&family=JetBrains+Mono:wght@300;400;500;600&family=Inter:wght@300;400;500;600&display=swap');

  @keyframes modalSlideIn {
    from {
      opacity: 0;
      transform: translateY(20px) scale(0.98);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }

  @keyframes scanLine {
    0% { opacity: 0.8; }
    50% { opacity: 0.4; }
    100% { opacity: 0.8; }
  }

  @keyframes glowPulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.6; }
  }

  @keyframes barReveal {
    from { transform: scaleY(0); }
    to { transform: scaleY(1); }
  }

  .cyber-receipt {
    position: relative;
    background: linear-gradient(180deg, #0a0a12 0%, #0f0f1a 50%, #0a0a12 100%);
    border: 1px solid rgba(255, 170, 0, 0.2);
    border-radius: 4px;
    padding: 0;
    overflow: hidden;
    font-family: 'Inter', sans-serif;
    color: #e0e0e8;
  }

  .scan-line {
    position: absolute;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(90deg, transparent, rgba(255,170,0,0.6), transparent);
    box-shadow: 0 0 10px rgba(255,170,0,0.8), 0 0 20px rgba(255,170,0,0.4);
    z-index: 10;
    pointer-events: none;
    animation: scanLine 0.5s ease-in-out infinite;
  }

  .circuit-pattern {
    position: absolute;
    inset: 0;
    background-image:
      linear-gradient(rgba(255,170,0,0.03) 1px, transparent 1px),
      linear-gradient(90deg, rgba(255,170,0,0.03) 1px, transparent 1px);
    background-size: 20px 20px;
    pointer-events: none;
  }

  /* Header */
  .receipt-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    padding: 24px 28px;
    border-bottom: 1px solid rgba(255,170,0,0.1);
    background: linear-gradient(180deg, rgba(255,170,0,0.05) 0%, transparent 100%);
  }

  .logo-container {
    display: flex;
    align-items: center;
    gap: 14px;
  }

  .logo-hex {
    width: 48px;
    height: 48px;
    background: linear-gradient(135deg, #ffaa00 0%, #cc8800 100%);
    clip-path: polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%);
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 0 20px rgba(255,170,0,0.4);
  }

  .logo-icon {
    width: 24px;
    height: 24px;
    color: #0a0a12;
  }

  .logo-text {
    display: flex;
    flex-direction: column;
  }

  .brand-name {
    font-family: 'Orbitron', sans-serif;
    font-weight: 800;
    font-size: 22px;
    letter-spacing: 4px;
    color: #ffaa00;
    text-shadow: 0 0 10px rgba(255,170,0,0.5);
  }

  .brand-sub {
    font-family: 'JetBrains Mono', monospace;
    font-size: 9px;
    letter-spacing: 3px;
    color: rgba(255,170,0,0.5);
    margin-top: 2px;
  }

  .header-right {
    text-align: right;
  }

  .doc-type {
    font-family: 'Orbitron', sans-serif;
    font-weight: 600;
    font-size: 11px;
    letter-spacing: 3px;
    color: rgba(255,255,255,0.4);
    margin-bottom: 4px;
  }

  .doc-id {
    font-family: 'JetBrains Mono', monospace;
    font-weight: 600;
    font-size: 16px;
    color: #ffaa00;
    letter-spacing: 1px;
  }

  /* Status Banner */
  .status-banner {
    display: flex;
    align-items: center;
    gap: 16px;
    margin: 20px 28px;
    padding: 16px 20px;
    border: 1px solid;
    border-radius: 4px;
    position: relative;
  }

  .status-icon-container {
    position: relative;
    width: 48px;
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .status-icon {
    width: 28px;
    height: 28px;
    z-index: 1;
  }

  .status-ring {
    position: absolute;
    inset: 0;
    border: 2px solid;
    border-radius: 50%;
    animation: glowPulse 2s ease-in-out infinite;
  }

  .status-text {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .status-label {
    font-family: 'Orbitron', sans-serif;
    font-weight: 700;
    font-size: 14px;
    letter-spacing: 2px;
  }

  .status-time {
    font-family: 'JetBrains Mono', monospace;
    font-size: 11px;
    color: rgba(255,255,255,0.4);
  }

  /* Code Display Section */
  .code-display-section {
    margin: 0 28px 20px;
    padding: 24px;
    background: linear-gradient(135deg, rgba(255,170,0,0.1) 0%, rgba(204,136,0,0.05) 100%);
    border: 2px solid rgba(255,170,0,0.3);
    border-radius: 8px;
    text-align: center;
  }

  .code-label {
    font-family: 'Orbitron', sans-serif;
    font-size: 10px;
    letter-spacing: 4px;
    color: rgba(255,170,0,0.6);
    margin-bottom: 12px;
  }

  .code-value-container {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 16px;
  }

  .code-value {
    font-family: 'Orbitron', sans-serif;
    font-size: 32px;
    font-weight: 800;
    color: #ffaa00;
    text-shadow: 0 0 20px rgba(255,170,0,0.5);
    letter-spacing: 4px;
  }

  .copy-button {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 16px;
    background: rgba(255,170,0,0.1);
    border: 1px solid rgba(255,170,0,0.3);
    border-radius: 4px;
    color: #ffaa00;
    font-family: 'JetBrains Mono', monospace;
    font-size: 11px;
    letter-spacing: 1px;
    cursor: pointer;
    transition: all 0.2s;
  }

  .copy-button:hover {
    background: rgba(255,170,0,0.2);
  }

  .code-description {
    margin-top: 12px;
    font-size: 13px;
    color: rgba(255,255,255,0.5);
  }

  /* Content Grid */
  .content-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
    padding: 0 28px;
  }

  .detail-section {
    background: rgba(0,0,0,0.3);
    border: 1px solid rgba(255,170,0,0.1);
    border-radius: 4px;
    overflow: hidden;
  }

  .section-header {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 14px;
    background: rgba(255,170,0,0.05);
    border-bottom: 1px solid rgba(255,170,0,0.1);
    font-family: 'Orbitron', sans-serif;
    font-weight: 600;
    font-size: 10px;
    letter-spacing: 2px;
    color: rgba(255,170,0,0.7);
  }

  .section-icon {
    width: 14px;
    height: 14px;
  }

  .detail-rows {
    padding: 14px;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .detail-row {
    display: flex;
    flex-direction: column;
    gap: 3px;
  }

  .detail-label {
    font-family: 'JetBrains Mono', monospace;
    font-size: 9px;
    letter-spacing: 1px;
    color: rgba(255,255,255,0.35);
  }

  .detail-value {
    font-size: 13px;
    color: #e0e0e8;
  }

  .detail-value.highlight {
    color: #ffffff;
    font-weight: 500;
  }

  .detail-value.mono {
    font-family: 'JetBrains Mono', monospace;
    font-size: 12px;
    color: rgba(255,170,0,0.8);
  }

  /* Progress Section */
  .progress-section {
    margin: 20px 28px;
    padding: 16px;
    background: rgba(0,0,0,0.3);
    border: 1px solid rgba(255,170,0,0.1);
    border-radius: 4px;
  }

  .progress-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
  }

  .progress-label {
    font-family: 'Orbitron', sans-serif;
    font-size: 10px;
    letter-spacing: 2px;
    color: rgba(255,170,0,0.7);
  }

  .progress-value {
    font-family: 'Orbitron', sans-serif;
    font-size: 14px;
    font-weight: 700;
    color: #ffaa00;
  }

  .progress-bar-container {
    height: 8px;
    background: rgba(255,170,0,0.1);
    border-radius: 4px;
    overflow: hidden;
  }

  .progress-bar-fill {
    height: 100%;
    background: linear-gradient(90deg, #ffaa00, #ff8800);
    border-radius: 4px;
    transition: width 0.5s ease;
  }

  .progress-text {
    margin-top: 8px;
    font-family: 'JetBrains Mono', monospace;
    font-size: 10px;
    color: rgba(255,255,255,0.4);
    text-align: center;
  }

  /* Stats Row */
  .stats-row {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
    padding: 20px 28px;
  }

  .stat-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px;
    background: rgba(0,0,0,0.3);
    border: 1px solid rgba(255,170,0,0.1);
    border-radius: 4px;
  }

  .stat-icon {
    width: 40px;
    height: 40px;
    background: rgba(255,170,0,0.1);
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #ffaa00;
  }

  .stat-content {
    display: flex;
    flex-direction: column;
  }

  .stat-value {
    font-family: 'Orbitron', sans-serif;
    font-size: 18px;
    font-weight: 700;
    color: #ffffff;
  }

  .stat-label {
    font-family: 'JetBrains Mono', monospace;
    font-size: 8px;
    letter-spacing: 1px;
    color: rgba(255,255,255,0.4);
  }

  /* Barcode */
  .barcode-section {
    padding: 20px 28px;
    text-align: center;
  }

  .barcode-container {
    display: flex;
    justify-content: center;
    align-items: flex-end;
    gap: 2px;
    height: 50px;
    margin-bottom: 8px;
  }

  .barcode-bar {
    background: linear-gradient(180deg, #ffaa00 0%, rgba(255,170,0,0.6) 100%);
    border-radius: 1px;
    animation: barReveal 0.5s ease-out forwards;
    transform-origin: bottom;
  }

  .barcode-bar.thin {
    width: 2px;
    height: 35px;
  }

  .barcode-bar.thick {
    width: 4px;
    height: 45px;
  }

  .barcode-text {
    font-family: 'JetBrains Mono', monospace;
    font-size: 12px;
    letter-spacing: 4px;
    color: rgba(255,170,0,0.6);
  }

  /* Footer */
  .receipt-footer {
    padding: 20px 28px 24px;
    background: rgba(0,0,0,0.2);
    border-top: 1px solid rgba(255,170,0,0.1);
  }

  .footer-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
    margin-bottom: 16px;
  }

  .footer-item {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .footer-label {
    font-family: 'JetBrains Mono', monospace;
    font-size: 8px;
    letter-spacing: 1px;
    color: rgba(255,255,255,0.3);
  }

  .footer-value {
    font-family: 'JetBrains Mono', monospace;
    font-size: 10px;
    color: rgba(255,255,255,0.6);
  }

  .footer-value.verification {
    color: #ffaa00;
    font-weight: 600;
  }

  .footer-line {
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(255,170,0,0.2), transparent);
    margin: 16px 0;
  }

  .footer-disclaimer {
    font-size: 9px;
    line-height: 1.6;
    color: rgba(255,255,255,0.3);
    text-align: center;
    margin-bottom: 16px;
  }

  .footer-brand {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 8px;
    font-family: 'Orbitron', sans-serif;
  }

  .powered-by {
    font-size: 8px;
    color: rgba(255,255,255,0.2);
    letter-spacing: 1px;
  }

  .brand {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 3px;
    color: rgba(255,170,0,0.6);
  }

  .year {
    font-size: 8px;
    color: rgba(255,255,255,0.2);
  }

  /* Corner decorations */
  .corner {
    position: absolute;
    width: 20px;
    height: 20px;
    border-color: rgba(255,170,0,0.4);
    border-style: solid;
    border-width: 0;
  }

  .corner-tl {
    top: 8px;
    left: 8px;
    border-top-width: 2px;
    border-left-width: 2px;
  }

  .corner-tr {
    top: 8px;
    right: 8px;
    border-top-width: 2px;
    border-right-width: 2px;
  }

  .corner-bl {
    bottom: 8px;
    left: 8px;
    border-bottom-width: 2px;
    border-left-width: 2px;
  }

  .corner-br {
    bottom: 8px;
    right: 8px;
    border-bottom-width: 2px;
    border-right-width: 2px;
  }
`;

// Print-specific styles
const printStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Orbitron:wght@400;500;600;700;800;900&family=JetBrains+Mono:wght@300;400;500;600&family=Inter:wght@300;400;500;600&display=swap');

  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  body {
    background: #ffffff;
    padding: 20px;
  }

  .cyber-receipt {
    position: relative;
    background: #ffffff;
    border: 2px solid #0a0a12;
    border-radius: 4px;
    padding: 0;
    overflow: hidden;
    font-family: 'Inter', sans-serif;
    color: #1a1a2e;
    max-width: 600px;
    margin: 0 auto;
  }

  .scan-line,
  .circuit-pattern {
    display: none;
  }

  .receipt-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    padding: 24px 28px;
    border-bottom: 2px solid #0a0a12;
    background: #f8f8fa;
  }

  .logo-container {
    display: flex;
    align-items: center;
    gap: 14px;
  }

  .logo-hex {
    width: 48px;
    height: 48px;
    background: #0a0a12;
    clip-path: polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%);
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .logo-icon {
    width: 24px;
    height: 24px;
    color: #ffffff;
  }

  .logo-text {
    display: flex;
    flex-direction: column;
  }

  .brand-name {
    font-family: 'Orbitron', sans-serif;
    font-weight: 800;
    font-size: 22px;
    letter-spacing: 4px;
    color: #0a0a12;
  }

  .brand-sub {
    font-family: 'JetBrains Mono', monospace;
    font-size: 9px;
    letter-spacing: 3px;
    color: #666;
    margin-top: 2px;
  }

  .header-right {
    text-align: right;
  }

  .doc-type {
    font-family: 'Orbitron', sans-serif;
    font-weight: 600;
    font-size: 11px;
    letter-spacing: 3px;
    color: #666;
    margin-bottom: 4px;
  }

  .doc-id {
    font-family: 'JetBrains Mono', monospace;
    font-weight: 600;
    font-size: 16px;
    color: #0a0a12;
    letter-spacing: 1px;
  }

  .status-banner {
    display: flex;
    align-items: center;
    gap: 16px;
    margin: 20px 28px;
    padding: 16px 20px;
    border: 2px solid #0a0a12;
    border-radius: 4px;
    background: #f8f8fa;
  }

  .status-icon-container {
    width: 48px;
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #0a0a12;
  }

  .status-icon {
    width: 28px;
    height: 28px;
  }

  .status-ring {
    display: none;
  }

  .status-text {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .status-label {
    font-family: 'Orbitron', sans-serif;
    font-weight: 700;
    font-size: 14px;
    letter-spacing: 2px;
    color: #0a0a12;
  }

  .status-time {
    font-family: 'JetBrains Mono', monospace;
    font-size: 11px;
    color: #666;
  }

  .code-display-section {
    margin: 0 28px 20px;
    padding: 24px;
    background: #f8f8fa;
    border: 2px solid #0a0a12;
    border-radius: 8px;
    text-align: center;
  }

  .code-label {
    font-family: 'Orbitron', sans-serif;
    font-size: 10px;
    letter-spacing: 4px;
    color: #666;
    margin-bottom: 12px;
  }

  .code-value-container {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 16px;
  }

  .code-value {
    font-family: 'Orbitron', sans-serif;
    font-size: 32px;
    font-weight: 800;
    color: #0a0a12;
    letter-spacing: 4px;
  }

  .copy-button {
    display: none;
  }

  .code-description {
    margin-top: 12px;
    font-size: 13px;
    color: #666;
  }

  .content-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
    padding: 0 28px;
  }

  .detail-section {
    background: #f8f8fa;
    border: 1px solid #ddd;
    border-radius: 4px;
    overflow: hidden;
  }

  .section-header {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 14px;
    background: #eee;
    border-bottom: 1px solid #ddd;
    font-family: 'Orbitron', sans-serif;
    font-weight: 600;
    font-size: 10px;
    letter-spacing: 2px;
    color: #333;
  }

  .section-icon {
    width: 14px;
    height: 14px;
  }

  .detail-rows {
    padding: 14px;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .detail-row {
    display: flex;
    flex-direction: column;
    gap: 3px;
  }

  .detail-label {
    font-family: 'JetBrains Mono', monospace;
    font-size: 9px;
    letter-spacing: 1px;
    color: #888;
  }

  .detail-value {
    font-size: 13px;
    color: #1a1a2e;
  }

  .detail-value.highlight {
    color: #000;
    font-weight: 600;
  }

  .detail-value.mono {
    font-family: 'JetBrains Mono', monospace;
    font-size: 12px;
    color: #333;
  }

  .progress-section {
    margin: 20px 28px;
    padding: 16px;
    background: #f8f8fa;
    border: 1px solid #ddd;
    border-radius: 4px;
  }

  .progress-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
  }

  .progress-label {
    font-family: 'Orbitron', sans-serif;
    font-size: 10px;
    letter-spacing: 2px;
    color: #666;
  }

  .progress-value {
    font-family: 'Orbitron', sans-serif;
    font-size: 14px;
    font-weight: 700;
    color: #0a0a12;
  }

  .progress-bar-container {
    height: 8px;
    background: #ddd;
    border-radius: 4px;
    overflow: hidden;
  }

  .progress-bar-fill {
    height: 100%;
    background: #333;
    border-radius: 4px;
  }

  .progress-text {
    margin-top: 8px;
    font-family: 'JetBrains Mono', monospace;
    font-size: 10px;
    color: #666;
    text-align: center;
  }

  .stats-row {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
    padding: 20px 28px;
  }

  .stat-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px;
    background: #f8f8fa;
    border: 1px solid #ddd;
    border-radius: 4px;
  }

  .stat-icon {
    width: 40px;
    height: 40px;
    background: #eee;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #333;
  }

  .stat-content {
    display: flex;
    flex-direction: column;
  }

  .stat-value {
    font-family: 'Orbitron', sans-serif;
    font-size: 18px;
    font-weight: 700;
    color: #0a0a12;
  }

  .stat-label {
    font-family: 'JetBrains Mono', monospace;
    font-size: 8px;
    letter-spacing: 1px;
    color: #888;
  }

  .barcode-section {
    padding: 20px 28px;
    text-align: center;
  }

  .barcode-container {
    display: flex;
    justify-content: center;
    align-items: flex-end;
    gap: 2px;
    height: 50px;
    margin-bottom: 8px;
  }

  .barcode-bar {
    background: #0a0a12;
    border-radius: 1px;
  }

  .barcode-bar.thin {
    width: 2px;
    height: 35px;
  }

  .barcode-bar.thick {
    width: 4px;
    height: 45px;
  }

  .barcode-text {
    font-family: 'JetBrains Mono', monospace;
    font-size: 12px;
    letter-spacing: 4px;
    color: #333;
  }

  .receipt-footer {
    padding: 20px 28px 24px;
    background: #f8f8fa;
    border-top: 2px solid #0a0a12;
  }

  .footer-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
    margin-bottom: 16px;
  }

  .footer-item {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .footer-label {
    font-family: 'JetBrains Mono', monospace;
    font-size: 8px;
    letter-spacing: 1px;
    color: #888;
  }

  .footer-value {
    font-family: 'JetBrains Mono', monospace;
    font-size: 10px;
    color: #333;
  }

  .footer-value.verification {
    color: #0a0a12;
    font-weight: 600;
  }

  .footer-line {
    height: 1px;
    background: #ddd;
    margin: 16px 0;
  }

  .footer-disclaimer {
    font-size: 9px;
    line-height: 1.6;
    color: #666;
    text-align: center;
    margin-bottom: 16px;
  }

  .footer-brand {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 8px;
    font-family: 'Orbitron', sans-serif;
  }

  .powered-by {
    font-size: 8px;
    color: #888;
    letter-spacing: 1px;
  }

  .brand {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 3px;
    color: #0a0a12;
  }

  .year {
    font-size: 8px;
    color: #888;
  }

  .corner {
    position: absolute;
    width: 20px;
    height: 20px;
    border-color: #0a0a12;
    border-style: solid;
    border-width: 0;
  }

  .corner-tl {
    top: 8px;
    left: 8px;
    border-top-width: 2px;
    border-left-width: 2px;
  }

  .corner-tr {
    top: 8px;
    right: 8px;
    border-top-width: 2px;
    border-right-width: 2px;
  }

  .corner-bl {
    bottom: 8px;
    left: 8px;
    border-bottom-width: 2px;
    border-left-width: 2px;
  }

  .corner-br {
    bottom: 8px;
    right: 8px;
    border-bottom-width: 2px;
    border-right-width: 2px;
  }
`;

export default InviteCodeDetailModal;
